/**
 * @class TreeNode
 * @extends Ofcs.Object
 */
export default class TreeNode {
    /**
     * @type {Bounds}
     */
    areaBounds = null;

    /**
     * @type {Bounds}
     */
    bounds = null;

    /**
     * @type {Line[]}
     */
    lines = null;

    /**
     * @type {number}
     */
    depth = null;

    /**
     * @type {TreeNode}
     */
    left = null;

    /**
     * @type {TreeNode}
     */
    right = null;

    /**
     * Get full bounds for this node.
     *
     * @param {Bounds} areaBounds
     * @param {Bounds} bounds
     * @param {Line[]} lines
     * @param {number} depth
     */
    constructor(areaBounds, bounds, lines, depth) {
        this.areaBounds = areaBounds;
        this.bounds = bounds;
        this.lines = lines;
        this.depth = depth;
        this.left = null;
        this.right = null;
    }

    /**
     * Get full bounds for this node.
     *
     * @return {Bounds}
     */
    getBounds() {
        return this.bounds;
    }

    /**
     * Get line based bounds for this node.
     *
     * @return {Bounds}
     */
    getAreaBounds() {
        return this.areaBounds;
    }

    /**
     * Return lines relevant for this node.
     *
     * @return {Line[]}
     */
    getLines() {
        return this.lines;
    }

    /**
     * Return depth of node.
     *
     * @return {number}
     */
    getDepth() {
        return this.depth;
    }

    /**
     * Is this node a leaf node?
     *
     * @return {boolean}
     */
    isLeaf() {
        return !this.hasLeft() && !this.hasRight();
    }

    /**
     * Return left child node.
     *
     * @return {TreeNode}
     */
    getLeft() {
        return this.left;
    }

    /**
     * Is a left child node set?
     *
     * @param {TreeNode} left
     */
    setLeft(left) {
        this.left = left;
    }

    /**
     * Is a left child node set?
     *
     * @return {boolean}
     */
    hasLeft() {
        return this.left !== null;
    }

    /**
     * Return right child node.
     *
     * @return {TreeNode}
     */
    getRight() {
        return this.right;
    }

    /**
     * Is a right child node set?
     *
     * @param {TreeNode} right
     */
    setRight(right) {
        this.right = right;
    }

    /**
     * Is a right child node set?
     *
     * @return {boolean}
     */
    hasRight() {
        return this.right !== null;
    }

    /**
     * Intersect this node with a tree.
     *
     * @param {Tree} tree
     * @param {Intersections} intersections
     * @return {boolean}
     */
    intersectTree(tree, intersections) {
        const node = tree.getRoot();

        return this._intersectNodeToNode(node, intersections);
    }

    /**
     * Intersect this node with another node.
     *
     * @param {TreeNode} node
     * @param {Intersections} intersections
     * @return {boolean}
     * @private
     */
    _intersectNodeToNode(node, intersections) {
        let result = false;

        if (node.isLeaf()) {
            result = true;
        }

        if (this.isLeaf() && node.isLeaf()) {
            this.intersectLines(node, intersections);
            return result;
        }

        if (node.hasLeft()) {
            const left = node.getLeft();
            if (this.intersectNode(left)) {
                if (left.isLeaf()) {
                    result = true;
                }
                if (this.isLeaf() && left.isLeaf()) {
                    this.intersectLines(left, intersections);
                }
                if (!left.isLeaf()) {
                    result = this._intersectNodeToNode(left, intersections) || result;
                }
            }
        }

        if (node.hasRight()) {
            const right = node.getRight();
            if (this.intersectNode(right)) {
                if (right.isLeaf()) {
                    result = true;
                }
                if (this.isLeaf() && right.isLeaf()) {
                    this.intersectLines(right, intersections);
                }
                if (!right.isLeaf()) {
                    result = this._intersectNodeToNode(right, intersections) || result;
                }
            }
        }

        return result;
    }

    /**
     * Intersect the bounds of this node with the bounds of another node.
     *
     * @param {TreeNode} other
     * @return {boolean}
     */
    intersectNode(other) {
        return this.getAreaBounds().intersect(other.getAreaBounds());
    }

    /**
     * Intersect the lines of this node with the lines of another node.
     *
     * @param {TreeNode} node
     * @param {Intersections} intersections
     * @return {boolean}
     */
    intersectLines(node, intersections) {
        // eslint-disable-next-line no-restricted-syntax
        for (const line of this.getLines()) {
            // eslint-disable-next-line no-restricted-syntax
            for (const otherLine of node.getLines()) {
                const result = line.intersect(otherLine);
                if (result !== false) {
                    if (result !== true) intersections.add(result);
                    return true;
                }
            }
        }

        return false;
    }
}
